import React from 'react';
import { Palette, CheckCircle2, AlertTriangle, Moon, Sun } from 'lucide-react';
import { useContrastChecker } from '../hooks/useContrastChecker';

export interface ThemeTokens {
  primary: string;
  background: string;
  foreground: string;
  accent: string;
  headingFont: string;
  bodyFont: string;
  darkMode: boolean;
}

export interface ThemeTokenEditorProps {
  tokens: ThemeTokens;
  onChange: (tokens: ThemeTokens) => void;
  onApplyPreset: (preset: 'school' | 'app' | 'coach') => void;
}

// ThemeTokenEditor manages semantic HSL tokens, typography pairings, and WCAG AA contrast validation.
export function ThemeTokenEditor({
  tokens,
  onChange,
  onApplyPreset,
}: ThemeTokenEditorProps): React.JSX.Element {
  const contrast = useContrastChecker(tokens.foreground, tokens.background);

  const updateColor = (key: 'primary' | 'background' | 'foreground' | 'accent', value: string) => {
    onChange({ ...tokens, [key]: value });
  };

  return (
    <div className="p-6 max-w-2xl bg-card border border-border rounded-xl flex flex-col gap-6">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Brand Theme & Design Tokens</h2>
        </div>
        <button
          type="button"
          onClick={() => onChange({ ...tokens, darkMode: !tokens.darkMode })}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-background text-sm font-medium hover:bg-muted transition-colors min-h-[44px]"
        >
          {tokens.darkMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          <span>{tokens.darkMode ? 'Dark' : 'Light'}</span>
        </button>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onApplyPreset('school')}
          className="px-3 py-1.5 rounded border border-border text-xs font-semibold hover:bg-muted text-foreground min-h-[44px]"
        >
          Heritage School
        </button>
        <button
          type="button"
          onClick={() => onApplyPreset('app')}
          className="px-3 py-1.5 rounded border border-border text-xs font-semibold hover:bg-muted text-foreground min-h-[44px]"
        >
          Enterprise SaaS
        </button>
        <button
          type="button"
          onClick={() => onApplyPreset('coach')}
          className="px-3 py-1.5 rounded border border-border text-xs font-semibold hover:bg-muted text-foreground min-h-[44px]"
        >
          Test-Prep Coach
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Primary (HSL)
          </label>
          <input
            type="text"
            value={tokens.primary}
            onChange={(e) => updateColor('primary', e.target.value)}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Accent (HSL)
          </label>
          <input
            type="text"
            value={tokens.accent}
            onChange={(e) => updateColor('accent', e.target.value)}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Background (HSL)
          </label>
          <input
            type="text"
            value={tokens.background}
            onChange={(e) => updateColor('background', e.target.value)}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Foreground (HSL)
          </label>
          <input
            type="text"
            value={tokens.foreground}
            onChange={(e) => updateColor('foreground', e.target.value)}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Heading Font Family
          </label>
          <input
            type="text"
            value={tokens.headingFont}
            placeholder="e.g. Playfair Display, Inter, Outfit"
            onChange={(e) => onChange({ ...tokens, headingFont: e.target.value })}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Body Font Family
          </label>
          <input
            type="text"
            value={tokens.bodyFont}
            placeholder="e.g. Inter, Roboto, Plus Jakarta Sans"
            onChange={(e) => onChange({ ...tokens, bodyFont: e.target.value })}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
          />
        </div>
      </div>

      <div className="p-4 rounded-lg bg-muted/30 border border-border flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">WCAG Contrast Ratio</span>
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          {contrast.passesAA ? (
            <CheckCircle2 className="h-4 w-4 text-accent" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-primary" />
          )}
          <span>{contrast.ratio}:1</span>
          <span className="text-xs text-muted-foreground">({contrast.passesAA ? 'Passes AA' : 'Fails AA'})</span>
        </div>
      </div>
    </div>
  );
}
